import * as React from 'react';
import PciAnnouncementModal from './PciAnnouncementModal';
import { AcceptTermsandConditionsDto, AcknowledgementV2Dto } from '@inosio/payments-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { getRecordAgreementDetails, selectOnboardingApplicationState } from '../../onboarding/sagas/pmonboardingSlice';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import TermsDialogWithConsent from '../../../components/TermsDialogWithConsent';
import { getExportAckForm, markAcknowledgementAsResolved, selectAcknowledgementsState } from '../sagas/acknowledgementsSlice';
import { PAYSAFE_2025_SAQ_A_ACK_UUID } from '../../../utils/constants';
import { useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

export default function PciAcknowledgement({ acknowledgement }: { acknowledgement: AcknowledgementV2Dto }) {
  const dispatch = useDispatch();

  const [openAnnouncemnetModal, setOpenAnnouncementModal] = React.useState(true);
  const [errorAcceptingTerms, setErrorAcceptingTerms] = useState('');

  const handleReviewForm = () => {
    dispatch({
      type: getExportAckForm.type,
      payload: {
        uuid: PAYSAFE_2025_SAQ_A_ACK_UUID,
      },
    });
  };

  const handleProceed = () => {
    setOpenAnnouncementModal(false);
    setOpenPciTerm(true);
  };

  // Start: Applicant Rental History Verification Consent Section
  const { pmDetails, isRecordingAgreementSuccess, isRecordingAgreementFailed } = useSelector(selectOnboardingApplicationState);
  const { isMarkingAcknowledgmentAsResolvedSuccess, isMarkingAcknowledgmentAsResolvedFailure } = useSelector(selectAcknowledgementsState);

  const PciTermText = `
    <br/><br/>
    <b>PCI SAQ-A Attestation Result:</b> Compliant
    <br/><br/>
    All sections of the PCI DSS SAQ are complete, all questions answered affirmatively, resulting in an overall COMPLIANT rating; thereby ${
      pmDetails?.Company?.DbaName || pmDetails?.Company?.LegalName
    } has demonstrated full compliance with the PCI DSS.
    <br/><br/>
    By Signing below you are stating that you are an authorized signatory for ${pmDetails?.Company?.DbaName || pmDetails?.Company?.LegalName} and you have reviewed and agreed to the form.<br/><br/>
  `;

  const [isAcceptingEVConsent, setIsAcceptingEVConsent] = React.useState(false);
  const [openPciTerm, setOpenPciTerm] = React.useState(false);
  const [checkBoxTicked, setCheckBoxTicked] = useState(false);
  const [signatureFullName, setSignatureFullName] = useState('');
  const metadata = acknowledgement?.Metadata as any;

  const handleConfirmPciTerm = () => {
    setIsSpinnerModalOpen(true);
    dispatch({
      type: getRecordAgreementDetails.type,
      payload: {
        Version: metadata.Version,
        Description: metadata.Description,
        TermsConditionsTypeId: +metadata.TermsConditionsTypeId,
        FullName: signatureFullName,
      } as AcceptTermsandConditionsDto,
    });
    setOpenPciTerm(false);
    setIsAcceptingEVConsent(true);
  };
  //handler for signature change in the consent modal
  const handleChangeSignature = (fullName: string) => {
    setSignatureFullName(fullName);
  };
  //handler for clicking the checkbox in the consent modal
  const handleCheckBoxClicked = () => {
    setCheckBoxTicked(!checkBoxTicked);
  };
  React.useEffect(() => {
    if (isAcceptingEVConsent) {
      if (isRecordingAgreementSuccess) {
        dispatch(markAcknowledgementAsResolved({ uuid: acknowledgement.Uuid }));
      } else if (isRecordingAgreementFailed) {
        setIsSpinnerModalOpen(false);
        setOpenPciTerm(true);
        setIsAcceptingEVConsent(false);
        setErrorAcceptingTerms('Something went wrong while accepting PCI terms. Please contact support');
        setFailureSnackBarOpen(true);
      }
    }
  }, [isAcceptingEVConsent, isRecordingAgreementSuccess, isRecordingAgreementFailed]);

  React.useEffect(() => {
    if (isMarkingAcknowledgmentAsResolvedSuccess && isAcceptingEVConsent) {
      setIsSpinnerModalOpen(false);
      setIsAcceptingEVConsent(false);
      setOpenPciTerm(false);
      dispatch({
        type: getExportAckForm.type,
        payload: {
          uuid: PAYSAFE_2025_SAQ_A_ACK_UUID,
        },
      });
    } else if (isMarkingAcknowledgmentAsResolvedFailure && isAcceptingEVConsent) {
      setIsSpinnerModalOpen(false);
      setOpenPciTerm(true);
      setIsAcceptingEVConsent(false);
      setErrorAcceptingTerms('Something went wrong while accepting PCI terms. Please contact support');
      setFailureSnackBarOpen(true);
    }
  }, [isMarkingAcknowledgmentAsResolvedSuccess, isMarkingAcknowledgmentAsResolvedFailure]);
  // End: Applicant Rental History Verification Consent Section

  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const [failureSnackBarOpen, setFailureSnackBarOpen] = useState(false);
  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setFailureSnackBarOpen(false);
  };

  return (
    <React.Fragment>
      <PciAnnouncementModal
        open={openAnnouncemnetModal}
        handleReview={handleReviewForm}
        handleProceed={handleProceed}
        handleClose={!acknowledgement.PreventsAppUsage ? () => setOpenAnnouncementModal(false) : undefined}
      />
      <TermsDialogWithConsent
        handleChangeSignature={handleChangeSignature}
        open={openPciTerm}
        scroll={true}
        text={PciTermText}
        acceptText={`I agree to the above`}
        title="Annual PCI Compliance Self-Attestation"
        handleConfirmConsent={handleConfirmPciTerm}
        buttonText="Agree"
        handleClose={!acknowledgement.PreventsAppUsage ? () => setOpenPciTerm(false) : undefined}
        signatureFullName={signatureFullName}
        handleCheckBoxClicked={handleCheckBoxClicked}
        checkboxTicked={checkBoxTicked}
      />
      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={failureSnackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {errorAcceptingTerms}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}
